import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Badge,
  LinearProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NfcIcon from '@mui/icons-material/Nfc';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import InfoIcon from '@mui/icons-material/Info';
import moment from 'moment';

// 模擬NFC讀取器
const NFCReader = ({ onScan, isScanning }) => {
  return (
    <Card sx={{ textAlign: 'center', p: 2, position: 'relative' }}>
      {isScanning && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', 
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 1 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress color="inherit" sx={{ mb: 1 }} />
            <Typography variant="body2">掃描中...</Typography>
            <Typography variant="caption">請將手機靠近NFC標籤</Typography>
          </Box>
        </Box>
      )}
      
      <NfcIcon sx={{ fontSize: 64, color: 'primary.main', mb: 1 }} />
      <Typography variant="h6" gutterBottom>NFC巡邏檢查點</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        點擊下方按鈕開始掃描NFC標籤
      </Typography>
      <Button 
        variant="contained" 
        startIcon={<NfcIcon />}
        onClick={onScan}
        disabled={isScanning}
      >
        開始掃描
      </Button>
    </Card>
  );
};

// 巡邏點卡片
const PatrolPointCard = ({ point, status, lastCheckedTime, onStartScan }) => {
  const getStatusIcon = () => {
    if (status === 'completed') {
      return <CheckIcon color="success" />;
    } else if (status === 'pending') {
      return <AccessTimeIcon color="action" />;
    } else if (status === 'late') {
      return <ErrorOutlineIcon color="error" />;
    } else {
      return <RadioButtonUncheckedIcon color="disabled" />;
    }
  };
  
  const getStatusChip = () => {
    if (status === 'completed') {
      return <Chip size="small" label="已檢查" color="success" />;
    } else if (status === 'pending') {
      return <Chip size="small" label="待檢查" color="primary" />;
    } else if (status === 'late') {
      return <Chip size="small" label="已逾時" color="error" />;
    } else {
      return <Chip size="small" label="待巡邏" color="default" />;
    }
  };
  
  return (
    <Card variant={status === 'pending' ? 'outlined' : 'elevation'}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar 
            sx={{ bgcolor: status === 'completed' ? 'success.light' : 
                          status === 'pending' ? 'primary.light' : 
                          status === 'late' ? 'error.light' : 'grey.300', 
                  mr: 1 }}
          >
            {getStatusIcon()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1">{point.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {point.location}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            {getStatusChip()}
          </Box>
        </Box>
        
        {status === 'completed' && (
          <Typography variant="body2" color="text.secondary">
            上次檢查: {lastCheckedTime}
          </Typography>
        )}
        
        {(status === 'pending' || status === 'late') && (
          <Button 
            variant="outlined" 
            size="small" 
            sx={{ mt: 1 }}
            startIcon={<NfcIcon />}
            onClick={() => onStartScan(point.id)}
            color={status === 'late' ? 'error' : 'primary'}
          >
            掃描檢查
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// 模擬NFC功能
const simulateNFCScanning = (pointId) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        id: pointId,
        scannedAt: new Date().toISOString(),
        success: true
      });
    }, 2000);
  });
};

const NFCPatrol = () => {
  const [patrolPoints, setPatrolPoints] = useState([]);
  const [currentPatrol, setCurrentPatrol] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [currentPointId, setCurrentPointId] = useState(null);
  const [showScanSuccess, setShowScanSuccess] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [completedPoints, setCompletedPoints] = useState([]);
  const [showPatrolComplete, setShowPatrolComplete] = useState(false);
  
  const dispatch = useDispatch();
  const { userRole } = useSelector(state => state.auth);
  
  // 載入巡邏點數據
  useEffect(() => {
    // 模擬API載入
    const mockPatrolPoints = [
      { id: 'p1', name: '正門入口', location: '地下大堂', scheduleTime: '每小時', status: 'pending' },
      { id: 'p2', name: '後門入口', location: '地下大堂', scheduleTime: '每2小時', status: 'pending' },
      { id: 'p3', name: '垃圾房', location: '地下一樓', scheduleTime: '每4小時', status: 'completed', lastChecked: '今天 09:15' },
      { id: 'p4', name: '停車場入口', location: '地下二樓', scheduleTime: '每2小時', status: 'late' },
      { id: 'p5', name: '電梯機房', location: '天台', scheduleTime: '每日', status: 'completed', lastChecked: '昨天 18:30' },
      { id: 'p6', name: '天台門', location: '天台', scheduleTime: '每日', status: 'completed', lastChecked: '昨天 18:35' },
    ];
    
    setPatrolPoints(mockPatrolPoints);
    
    // 模擬當前巡邏
    setCurrentPatrol({
      id: 'patrol123',
      name: '晚間安全巡邏',
      startTime: '今天 20:00',
      endTime: '今天 22:00',
      totalPoints: mockPatrolPoints.length,
      completedPoints: mockPatrolPoints.filter(p => p.status === 'completed').length,
      progress: (mockPatrolPoints.filter(p => p.status === 'completed').length / mockPatrolPoints.length) * 100
    });
  }, []);
  
  // 開始掃描
  const handleStartScan = (pointId) => {
    setIsScanning(true);
    setCurrentPointId(pointId);
    
    // 模擬NFC掃描過程 (實際應用中會使用真實的NFC API)
    simulateNFCScanning(pointId).then(result => {
      // 更新巡邏點狀態
      const updatedPoints = patrolPoints.map(p => {
        if (p.id === pointId) {
          return { ...p, status: 'completed', lastChecked: '剛剛' };
        }
        return p;
      });
      
      setPatrolPoints(updatedPoints);
      setCompletedPoints([...completedPoints, pointId]);
      setScanResult(result);
      
      // 更新巡邏進度
      setCurrentPatrol({
        ...currentPatrol,
        completedPoints: completedPoints.length + 1,
        progress: ((completedPoints.length + 1) / currentPatrol.totalPoints) * 100
      });
      
      setIsScanning(false);
      setShowScanSuccess(true);
      
      // 檢查是否巡邏完成
      if (completedPoints.length + 1 === currentPatrol.totalPoints) {
        setTimeout(() => {
          setShowPatrolComplete(true);
        }, 1000);
      }
    });
  };
  
  // 模擬掃描
  const simulateScan = () => {
    // 找出下一個待巡邏點
    const nextPoint = patrolPoints.find(p => p.status === 'pending' || p.status === 'late');
    if (nextPoint) {
      handleStartScan(nextPoint.id);
    }
  };
  
  // 巡邏完成處理
  const handlePatrolComplete = () => {
    setShowPatrolComplete(false);
    // 這裡可以進行完成後的邏輯處理，如提交巡邏報告等
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>NFC 巡邏</Typography>
      
      {currentPatrol && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">{currentPatrol.name}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {currentPatrol.startTime} - {currentPatrol.endTime}
            </Typography>
            
            <Box sx={{ mt: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2">
                  巡邏進度: {currentPatrol.completedPoints}/{currentPatrol.totalPoints}
                </Typography>
                <Typography variant="body2" sx={{ ml: 'auto' }}>
                  {Math.round(currentPatrol.progress)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={currentPatrol.progress} 
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <NFCReader onScan={simulateScan} isScanning={isScanning} />
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle1" gutterBottom>巡邏檢查點</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {patrolPoints.map(point => (
              <PatrolPointCard 
                key={point.id}
                point={point}
                status={point.status}
                lastCheckedTime={point.lastChecked}
                onStartScan={handleStartScan}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
      
      {/* 掃描成功對話框 */}
      <Dialog
        open={showScanSuccess}
        onClose={() => setShowScanSuccess(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            掃描成功
          </Typography>
          {scanResult && (
            <>
              <Typography variant="body1">
                {scanResult.pointName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {scanResult.scannedAt}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScanSuccess(false)}>
            確定
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 巡邏完成對話框 */}
      <Dialog
        open={showPatrolComplete}
        onClose={handlePatrolComplete}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Avatar sx={{ bgcolor: 'success.main', width: 80, height: 80, mb: 2 }}>
              <CheckIcon sx={{ fontSize: 48 }} />
            </Avatar>
            <Typography variant="h5" gutterBottom>
              巡邏完成!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              所有檢查點已完成巡邏
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'left', mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              巡邏報告
            </Typography>
            <Typography variant="body2">
              開始時間: {currentPatrol?.startTime}
            </Typography>
            <Typography variant="body2">
              完成時間: {moment().format('HH:mm')}
            </Typography>
            <Typography variant="body2">
              巡邏點數量: {currentPatrol?.totalPoints}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePatrolComplete}>
            完成
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NFCPatrol; 